// The week: a handful of outcomes per department ("frame the farm stand", "ship v0.1.22", "file
// GST"), rolling like the day plan — an outcome not hit by Sunday carries into next week on its own.
// Time budgets are optional and off unless set. The Sunday review is the seven Daily Logs rolled up.
// Pure — no I/O.
//
// weekPlans/{YYYY-Www}: { id, outcomes: [{ id, originId, departmentId, title, milestoneId, taskIds,
//   budgetMs, done, doneAt, carriedFrom, createdAt }], note, createdAt, updatedAt }

import { buildDayReport } from './dayreport.js';
import { GENERAL_DEPARTMENT_ID } from './hierarchy.js';

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** ISO week key of a local date ("2026-09-23" → "2026-W39"). Weeks run Monday to Sunday. */
export function weekKey(date) {
  const d = typeof date === 'string' ? new Date(date + 'T12:00:00') : new Date(date);
  const day = (d.getDay() + 6) % 7;                 // Mon = 0
  const thu = new Date(d); thu.setDate(d.getDate() - day + 3);
  const jan4 = new Date(thu.getFullYear(), 0, 4);
  const week = 1 + Math.round(((thu - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
  return `${thu.getFullYear()}-W${pad(week)}`;
}

/** The seven local dates (Mon..Sun) of a week key. */
export function weekDays(key) {
  const [y, w] = key.split('-W').map(Number);
  const jan4 = new Date(y, 0, 4, 12);
  const mon = new Date(jan4); mon.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (w - 1) * 7);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return ymd(d); });
}

export function shiftWeek(key, n) {
  const d = new Date(weekDays(key)[0] + 'T12:00:00');
  d.setDate(d.getDate() + 7 * n);
  return weekKey(d);
}

export function makeOutcome(fields = {}, now = Date.now()) {
  return {
    id: fields.id || `o${now.toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    originId: fields.originId || null,
    departmentId: fields.departmentId || null,
    title: String(fields.title || '').trim(),
    milestoneId: fields.milestoneId || null,
    taskIds: Array.isArray(fields.taskIds) ? fields.taskIds : [],
    budgetMs: fields.budgetMs || null,
    done: !!fields.done,
    doneAt: fields.doneAt || null,
    carriedFrom: fields.carriedFrom || null,
    createdAt: fields.createdAt || now,
  };
}

/**
 * Carry last week's unfinished outcomes into this week. Returns the outcomes this week should
 * have, or null when nothing changes. An outcome carries once (matched by originId) and keeps its
 * links; the carried copy remembers the week it came from.
 */
export function carryOutcomes(prevPlan, curPlan, prevKey) {
  const cur = curPlan?.outcomes || [];
  const have = new Set(cur.map((o) => o.originId || o.id));
  const add = (prevPlan?.outcomes || [])
    .filter((o) => !o.done && !have.has(o.originId || o.id))
    .map((o) => ({ ...o, id: `${o.originId || o.id}_${prevKey || 'prev'}`, originId: o.originId || o.id, carriedFrom: prevKey || o.carriedFrom || null }));
  return add.length ? [...cur, ...add] : null;
}

/** How many weeks an outcome has been carried (0 = planned this week). */
export function weeksCarried(outcome, key) {
  if (!outcome?.carriedFrom) return 0;
  let n = 0; let k = key;
  while (n < 52 && k > outcome.carriedFrom) { k = shiftWeek(k, -1); n += 1; }
  return Math.max(1, n);
}

/** Progress of an outcome from its links: linked tasks done, or its milestone completed. */
export function outcomeProgress(outcome, tasks = [], milestones = []) {
  const ids = new Set(outcome.taskIds || []);
  const linked = tasks.filter((t) => ids.has(t.id));
  const doneTasks = linked.filter((t) => t.status === 'done').length;
  const ms = outcome.milestoneId ? milestones.find((m) => m.id === outcome.milestoneId) : null;
  const total = linked.length + (ms ? 1 : 0);
  const done = doneTasks + (ms?.completed ? 1 : 0);
  return { done, total, pct: total ? Math.round((done / total) * 100) : (outcome.done ? 100 : 0), complete: total > 0 && done === total };
}

/** Task ids that feed any open outcome this week (the day plan ranks them first within a project). */
export function outcomeTaskIds(plan) {
  const s = new Set();
  (plan?.outcomes || []).filter((o) => !o.done).forEach((o) => (o.taskIds || []).forEach((id) => s.add(id)));
  return s;
}

/**
 * The Sunday review: seven day reports rolled up by department and by day, outcomes hit / missed,
 * and budget vs actual where a budget was set (actual = time on the outcome's linked tasks that week).
 */
export function buildWeekReport({ key, tasks = [], sessions = [], notes = [], flow = [], projects = [], departments = [], plan = null, milestones = [], now = Date.now(), departmentMode = false, pay = null, jobs = [], overhead = null, members = [], uid = null }) {
  const days = weekDays(key).map((date) => ({ date, r: buildDayReport({ date, tasks, sessions, notes, flow, projects, departments, now, departmentMode, pay, jobs, overhead, members, uid }) }));
  const tot = { clockedMs: 0, workedMs: 0, overheadMs: 0, travelMs: 0, breakMs: 0, done: 0, travelKm: 0 };
  const dept = new Map();
  const row = (id) => { if (!dept.has(id)) dept.set(id, { departmentId: id, clockedMs: 0, workedMs: 0, travelMs: 0, done: 0 }); return dept.get(id); };
  days.forEach(({ r }) => {
    tot.clockedMs += r.clock.clockedMs; tot.workedMs += r.time.workedMs; tot.overheadMs += r.time.setupMs + r.time.cleanupMs;
    tot.travelMs += r.travel.ms; tot.travelKm += r.travel.km; tot.breakMs += r.breaks.ms; tot.done += r.tasks.doneCount;
    r.byDepartment.forEach((x) => { const w = row(x.departmentId); w.clockedMs += x.clockedMs; w.workedMs += x.workedMs; w.travelMs += x.travelMs; w.done += x.done; });
  });
  tot.travelKm = Math.round(tot.travelKm * 10) / 10;
  tot.focus = tot.clockedMs > 0 ? Math.min(1, tot.workedMs / tot.clockedMs) : 0;

  const dates = new Set(days.map((d) => d.date));
  const outcomes = (plan?.outcomes || []).map((o) => {
    const prog = outcomeProgress(o, tasks, milestones);
    const ids = new Set(o.taskIds || []);
    const actualMs = tasks.filter((t) => ids.has(t.id)).reduce((s, t) => s + (t.timeEntries || []).filter((e) => dates.has(e.date)).reduce((a, e) => a + (e.duration || 0), 0), 0);
    return { ...o, progress: prog, hit: !!o.done || prog.complete, actualMs, overBudget: o.budgetMs ? actualMs > o.budgetMs : false };
  });
  const byDepartment = [...dept.values()].sort((a, b) => b.clockedMs - a.clockedMs || b.workedMs - a.workedMs);
  // The money row (CFO 5.7): each day's earned and profit both ways, billable ÷ clocked — only with a pay map.
  const moneyOf = (p) => (p ? { earned: p.total.earned, profit: p.total.profit, billable: p.utilization.billable, clocked: p.utilization.clocked, pct: p.utilization.pct } : null);
  let money = null;
  if (pay) {
    const ms = days.map(({ r }) => moneyOf(r.profit)).filter(Boolean);
    const sum = (f) => Math.round(ms.reduce((a, x) => a + f(x), 0) * 100) / 100;
    const billable = sum((x) => x.billable); const clocked = sum((x) => x.clocked);
    money = { earned: sum((x) => x.earned), profit: { actual: sum((x) => x.profit.actual), paidOut: sum((x) => x.profit.paidOut) }, billable, clocked, pct: clocked > 0 ? Math.round((billable / clocked) * 100) : null };
  }
  return {
    key, days: days.map(({ date, r }) => ({ date, clockedMs: r.clock.clockedMs, workedMs: r.time.workedMs, done: r.tasks.doneCount, empty: r.empty, money: moneyOf(r.profit) })),
    totals: { ...tot, money }, byDepartment, outcomes,
    hit: outcomes.filter((o) => o.hit).length, missed: outcomes.filter((o) => !o.hit).length,
    departmentsWithOutcomes: [...new Set(outcomes.map((o) => o.departmentId || GENERAL_DEPARTMENT_ID))],
  };
}
